import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { toast } from "vue-sonner";

import type { ConfidenceVoteSession } from "@/backend/natsstubs/confidence_vote/models";
import type { Unsubscribable } from "@/backend/read/BackendReadSession";
import { useGlobalI18n } from "@/translations/i18n";

import { useSessionStore } from "./session";
import { useUserStore } from "./user";

type DashboardView = "list" | "create" | "edit";

export const useConfidenceVoteStore = defineStore("confidenceVote", () => {
  // === STATE ===
  const sessions = ref<ConfidenceVoteSession[]>([]);
  const selectedSession = ref<ConfidenceVoteSession | null>(null);
  const currentView = ref<DashboardView>("list");
  const isLoaded = ref(false);

  // === GETTERS ===
  const activeSessions = computed(() => sessions.value.filter((s) => s.status === "active"));
  const endedSessions = computed(() => sessions.value.filter((s) => s.status === "ended"));
  const draftSessions = computed(() => sessions.value.filter((s) => s.status === "draft"));

  // === ACTIONS ===
  function setSessions(newSessions: ConfidenceVoteSession[]) {
    sessions.value = newSessions;
    isLoaded.value = true;
  }

  function addSession(session: ConfidenceVoteSession) {
    if (!sessions.value.some((s) => s.id === session.id)) {
      sessions.value = [session, ...sessions.value];
    }
  }

  function updateSession(sessionId: string, data: Partial<ConfidenceVoteSession>) {
    sessions.value = sessions.value.map((s) => (s.id === sessionId ? { ...s, ...data } : s));
    if (selectedSession.value?.id === sessionId) {
      selectedSession.value = { ...selectedSession.value, ...data };
    }
  }

  function removeSession(sessionId: string) {
    sessions.value = sessions.value.filter((s) => s.id !== sessionId);
  }

  function canVote(userId: string, session: ConfidenceVoteSession): boolean {
    const { role, teams } = useUserStore().technicalUser;

    if (role === "observer") return true;
    if (role === "admin" || role === "planning_interval_admin") return true;
    if (session.createdByUserId === userId) return true;
    if (session.participatingTeamIds.some((id) => teams.some((t) => t.id === id))) return true;

    return false;
  }

  function canManageSession(userId: string, session: ConfidenceVoteSession): boolean {
    const user = useUserStore().technicalUser;
    const isCreator = session.createdByUserId === userId;
    const hasCorrectRole = ["admin", "planning_interval_admin"].includes(user.role);
    return isCreator || hasCorrectRole;
  }

  // this is a workaround so the user does not see sessions that they are
  // not allowed to see when the session is created and broadcasted to everyone
  function canViewSession(session: ConfidenceVoteSession): boolean {
    const { role, id: userId, teams } = useUserStore().technicalUser;

    if (role === "admin" || role === "planning_interval_admin") return true;
    if (session.createdByUserId === userId) return true;
    if (session.status === "draft") return false;
    if (role === "observer") return true;

    return session.participatingTeamIds.some((id) => teams.some((t) => t.id === id));
  }

  function canDelete(sessionId: string, userId: string): boolean {
    const session = sessions.value.find((s) => s.id === sessionId);
    if (!session || session.status === "active") return false;
    return canManageSession(userId, session);
  }

  function clear() {
    sessions.value = [];
    isLoaded.value = false;
    currentView.value = "list";
    selectedSession.value = null;
  }

  function setSelectedSession(session: ConfidenceVoteSession | null) {
    selectedSession.value = session;
  }

  function setCurrentView(view: DashboardView) {
    currentView.value = view;
  }

  function selectSession(session: ConfidenceVoteSession) {
    selectedSession.value = session;
    if (session.status === "draft") {
      currentView.value = "edit";
    }
  }

  function setupSubscriptions(
    backendSess: {
      pubsubSubscribe: (
        topic: string,
        handler: (args: unknown, payload: unknown) => void,
      ) => Unsubscribable;
    },
    openSession: (sessionId: string) => void,
  ) {
    const { t } = useGlobalI18n();

    backendSess.pubsubSubscribe("confidence_vote.v1.sessions.created", (_args, payload) => {
      const session = payload as ConfidenceVoteSession;

      if (!canViewSession(session)) return;

      addSession(session);

      const userStore = useUserStore();
      if (session.createdByUserId !== userStore.technicalUser.id) {
        showConfidenceVoteInvitation(session, userStore.technicalUser, t, openSession);
      }
    });

    backendSess.pubsubSubscribe("confidence_vote.v1.sessions.*.deleted", (_args, payload) => {
      const event = payload as { votingSessionId: string };
      removeSession(event.votingSessionId);
    });
  }

  // Watch for PI session changes and clear store
  watch(
    () => useSessionStore().current?.id,
    (newSessionId, oldSessionId) => {
      if (oldSessionId && newSessionId !== oldSessionId) {
        clear();
      }
    },
  );

  function showConfidenceVoteInvitation(
    session: ConfidenceVoteSession,
    currentUser: { id: string; teams: Array<{ id: string }> },
    t: (key: string) => string,
    openSession: (sessionId: string) => void,
  ) {
    if (session.createdByUserId === currentUser.id) {
      return;
    }

    const currentUserTeams = currentUser.teams;
    if (
      !session.participatingTeamIds.some((teamId) =>
        currentUserTeams.some((team) => team.id === teamId),
      )
    ) {
      return;
    }

    toast.info(t("confidenceVote.inviteToastMessage"), {
      description: `"${session.title}"`,
      duration: Infinity,
      position: "top-right",
      closeButton: true,
      action: {
        label: t("confidenceVote.inviteToastAction"),
        onClick: () => {
          openSession(session.id);
        },
      },
    });
  }

  return {
    // State
    sessions,
    isLoaded,
    selectedSession,
    currentView,
    // Getters
    activeSessions,
    endedSessions,
    draftSessions,
    // Actions
    setSessions,
    addSession,
    updateSession,
    removeSession,
    canVote,
    canManageSession,
    canDelete,
    clear,
    setSelectedSession,
    setCurrentView,
    selectSession,
    // Subscriptions
    setupSubscriptions,
  };
});
